// src/pages/PrescriptionManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import nooLogo from '../assets/noo.png';
import leftArrowIcon from '../assets/left.png';

const displayFont = { fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" };
const bodyFont = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };
const monoFont = { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" };

const PrescriptionManagement = () => {
  const mockDoctors = [
    { id: 'doc1', name: 'Dr. Ahmed Hassan' },
    { id: 'doc2', name: 'Dr. Sara Ali' },
    { id: 'doc3', name: 'Dr. Omar Mahmoud' },
  ];

  const mockPatients = [
    { id: 'pat001', name: 'Nour Ali' },
    { id: 'pat002', name: 'Fatma Khaled' },
    { id: 'pat003', name: 'Mohamed Tarek' },
  ];

  const mockMedications = [
    { id: 'med1', name: 'Omeprazole 20mg', unit: 'capsule' },
    { id: 'med2', name: 'Loperamide 2mg', unit: 'tablet' },
    { id: 'med3', name: 'Metronidazole 500mg', unit: 'tablet' },
    { id: 'med4', name: 'Ranitidine 150mg', unit: 'tablet' },
    { id: 'med5', name: 'Buscopan 10mg', unit: 'tablet' },
  ];

  const [prescriptions, setPrescriptions] = useState(() => {
    const savedPrescriptions = localStorage.getItem('clinicPrescriptions');
    return savedPrescriptions ? JSON.parse(savedPrescriptions) : [];
  });

  useEffect(() => {
    localStorage.setItem('clinicPrescriptions', JSON.stringify(prescriptions));
  }, [prescriptions]);

  const [showModal, setShowModal] = useState(false);
  const [currentPrescription, setCurrentPrescription] = useState(null);

  const [formPatientId, setFormPatientId] = useState('');
  const [formDoctorId, setFormDoctorId] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formMedications, setFormMedications] = useState([{ medicationId: '', dosage: '', frequency: '', duration: '', notes: '' }]);
  const [formGeneralNotes, setFormGeneralNotes] = useState('');

  const [filterPatientId, setFilterPatientId] = useState('All');

  const handleAddPrescription = () => {
    setCurrentPrescription(null);
    setFormPatientId('');
    setFormDoctorId('');
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormMedications([{ medicationId: '', dosage: '', frequency: '', duration: '', notes: '' }]);
    setFormGeneralNotes('');
    setShowModal(true);
  };

  const handleEditPrescription = (prescription) => {
    setCurrentPrescription(prescription);
    setFormPatientId(prescription.patientId);
    setFormDoctorId(prescription.doctorId);
    setFormDate(prescription.date);
    setFormMedications(prescription.medications);
    setFormGeneralNotes(prescription.generalNotes || '');
    setShowModal(true);
  };

  const handleAddMedicationToForm = () => {
    setFormMedications([...formMedications, { medicationId: '', dosage: '', frequency: '', duration: '', notes: '' }]);
  };

  const handleMedicationChange = (index, field, value) => {
    const updatedMedications = [...formMedications];
    updatedMedications[index] = { ...updatedMedications[index], [field]: value };
    setFormMedications(updatedMedications);
  };

  const handleRemoveMedicationFromForm = (index) => {
    const updatedMedications = formMedications.filter((_, i) => i !== index);
    setFormMedications(updatedMedications);
  };

  const handleSubmitPrescription = (e) => {
    e.preventDefault();
    const patient = mockPatients.find(p => p.id === formPatientId);
    const doctor = mockDoctors.find(d => d.id === formDoctorId);

    if (!patient || !doctor) {
      alert('Please select a valid patient and doctor.');
      return;
    }

    const newPrescription = {
      id: currentPrescription ? currentPrescription.id : `presc${Date.now()}`,
      patientId: formPatientId,
      patientName: patient.name,
      doctorId: formDoctorId,
      doctorName: doctor.name,
      date: formDate,
      medications: formMedications.map(med => ({
        ...med,
        medicationName: mockMedications.find(mm => mm.id === med.medicationId)?.name || 'Unknown Medication',
        unit: mockMedications.find(mm => mm.id === med.medicationId)?.unit || ''
      })),
      generalNotes: formGeneralNotes,
    };

    if (currentPrescription) {
      setPrescriptions(prescriptions.map(presc => presc.id === newPrescription.id ? newPrescription : presc));
    } else {
      setPrescriptions([newPrescription, ...prescriptions]);
    }
    setShowModal(false);
  };

  const handleDeletePrescription = (id) => {
    if (window.confirm('Are you sure you want to delete this prescription?')) {
      setPrescriptions(prescriptions.filter(presc => presc.id !== id));
    }
  };

  const filteredPrescriptions = prescriptions.filter(presc =>
    filterPatientId === 'All' || presc.patientId === filterPatientId
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]" style={bodyFont}>
      {/* Header */}
      <div className="bg-white border-b border-[#E4E1D8]">
        <div className="container mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/home">
              <img src={nooLogo} alt="Drogastro" className="w-12 h-12 object-contain" />
            </Link>
            <div>
              <p className="text-[11px] tracking-wider text-[#0F3D3E]/50" style={monoFont}>PHARMACY</p>
              <h1 className="text-xl md:text-2xl font-bold text-[#0F3D3E]" style={displayFont}>Prescription Management</h1>
            </div>
          </div>
          <Link
            to="/home"
            className="flex items-center gap-2 bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#0F3D3E] font-semibold py-2 px-4 rounded-md border border-[#E4E1D8] transition-colors duration-200 text-sm"
          >
            <img src={leftArrowIcon} alt="Back" className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-sm p-5 md:p-8">
          {/* Controls */}
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center mb-6 gap-4">
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <label htmlFor="filter-patient" className="text-xs font-semibold text-[#0F3D3E]/70 whitespace-nowrap" style={monoFont}>PATIENT</label>
              <select
                id="filter-patient"
                value={filterPatientId}
                onChange={(e) => setFilterPatientId(e.target.value)}
                className="p-2 border border-[#E4E1D8] rounded-md w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
              >
                <option value="All">All Patients</option>
                {mockPatients.map(patient => (
                  <option key={patient.id} value={patient.id}>{patient.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleAddPrescription}
              className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200 w-full md:w-auto"
            >
              + Create New Prescription
            </button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-[#0F3D3E]" style={displayFont}>All Prescriptions</h2>
            <span className="text-xs font-semibold text-[#0F3D3E]/50 bg-[#FAF9F6] border border-[#E4E1D8] rounded-full px-3 py-1" style={monoFont}>
              {filteredPrescriptions.length} SHOWN
            </span>
          </div>

          {filteredPrescriptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-14 px-6 bg-[#FAF9F6] rounded-lg border border-dashed border-[#E4E1D8]">
              <p className="text-[#22262B]/60 text-base mb-1">No prescriptions found.</p>
              <button onClick={handleAddPrescription} className="text-[#4FB3A9] font-semibold text-sm hover:underline mt-1">
                Create the first one
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPrescriptions.map(presc => (
                <div key={presc.id} className="border border-[#E4E1D8] rounded-lg p-4 md:p-5 hover:border-[#4FB3A9] transition-colors">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-[11px] text-[#0F3D3E]/50 mb-0.5" style={monoFont}>{presc.id} · {presc.date}</p>
                      <p className="font-semibold text-[#0F3D3E]">{presc.patientName}</p>
                      <p className="text-sm text-[#22262B]/60">{presc.doctorName}</p>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => handleEditPrescription(presc)} className="text-[#0F3D3E] hover:text-[#4FB3A9] font-medium text-sm transition-colors">
                        Edit
                      </button>
                      <button onClick={() => handleDeletePrescription(presc.id)} className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors">
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="bg-[#FAF9F6] rounded-md p-3 space-y-1.5">
                    {presc.medications.map((med, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#4FB3A9] mt-1.5 shrink-0" />
                        <p className="text-[#22262B]">
                          <span className="font-semibold text-[#0F3D3E]">{med.medicationName}</span>
                          {' — '}{med.dosage} {med.unit}, {med.frequency}, {med.duration}
                          {med.notes && <span className="text-[#22262B]/60"> ({med.notes})</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                  {presc.generalNotes && (
                    <p className="text-sm text-[#22262B]/70 mt-3">
                      <span className="font-semibold text-[#0F3D3E]/70" style={monoFont}>NOTES: </span>
                      {presc.generalNotes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Prescription Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-[#0F3D3E]/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-8 pt-7 pb-5 border-b border-[#E4E1D8] sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-[#0F3D3E]" style={displayFont}>
                {currentPrescription ? 'Edit Prescription' : 'Create New Prescription'}
              </h2>
            </div>
            <form onSubmit={handleSubmitPrescription} className="space-y-5 px-8 py-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="modal-patient-select" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>PATIENT</label>
                  <select
                    id="modal-patient-select"
                    value={formPatientId}
                    onChange={(e) => setFormPatientId(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    required
                  >
                    <option value="">Select Patient</option>
                    {mockPatients.map(patient => (
                      <option key={patient.id} value={patient.id}>{patient.name} (ID: {patient.id})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="modal-doctor-select" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>DOCTOR</label>
                  <select
                    id="modal-doctor-select"
                    value={formDoctorId}
                    onChange={(e) => setFormDoctorId(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    required
                  >
                    <option value="">Select Doctor</option>
                    {mockDoctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="modal-prescription-date" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>DATE</label>
                <input
                  type="date"
                  id="modal-prescription-date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                />
              </div>

              {/* Medications */}
              <div className="border border-[#E4E1D8] rounded-lg bg-[#FAF9F6] overflow-hidden">
                <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#0F3D3E] tracking-wide" style={monoFont}>MEDICATIONS</h3>
                  <button
                    type="button"
                    onClick={handleAddMedicationToForm}
                    className="text-[#4FB3A9] hover:text-[#0F3D3E] font-semibold text-sm transition-colors"
                  >
                    + Add Medication
                  </button>
                </div>

                <div className="px-4 pb-4 space-y-3">
                  {formMedications.map((med, index) => (
                    <div key={index} className="bg-white border border-[#E4E1D8] rounded-md p-3 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label htmlFor={`med-select-${index}`} className="block text-[#22262B]/70 text-xs font-semibold mb-1">Medication</label>
                          <select
                            id={`med-select-${index}`}
                            value={med.medicationId}
                            onChange={(e) => handleMedicationChange(index, 'medicationId', e.target.value)}
                            className="w-full p-2 border border-[#E4E1D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                            required
                          >
                            <option value="">Select Medication</option>
                            {mockMedications.map(m => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`dosage-${index}`} className="block text-[#22262B]/70 text-xs font-semibold mb-1">Dosage</label>
                          <input
                            type="text"
                            id={`dosage-${index}`}
                            value={med.dosage}
                            onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                            className="w-full p-2 border border-[#E4E1D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                            placeholder="e.g., 1 tablet"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label htmlFor={`frequency-${index}`} className="block text-[#22262B]/70 text-xs font-semibold mb-1">Frequency</label>
                          <input
                            type="text"
                            id={`frequency-${index}`}
                            value={med.frequency}
                            onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                            className="w-full p-2 border border-[#E4E1D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                            placeholder="e.g., Twice daily"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor={`duration-${index}`} className="block text-[#22262B]/70 text-xs font-semibold mb-1">Duration</label>
                          <input
                            type="text"
                            id={`duration-${index}`}
                            value={med.duration}
                            onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                            className="w-full p-2 border border-[#E4E1D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                            placeholder="e.g., 7 days"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor={`notes-${index}`} className="block text-[#22262B]/70 text-xs font-semibold mb-1">Notes (optional)</label>
                        <textarea
                          id={`notes-${index}`}
                          value={med.notes}
                          onChange={(e) => handleMedicationChange(index, 'notes', e.target.value)}
                          className="w-full p-2 border border-[#E4E1D8] rounded-md h-16 resize-none text-sm focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                          placeholder="Specific instructions for this medication..."
                        ></textarea>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveMedicationFromForm(index)}
                          className="text-red-500 hover:text-red-700 font-medium text-sm transition-colors"
                        >
                          Remove Medication
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="modal-general-notes" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>GENERAL NOTES</label>
                <textarea
                  id="modal-general-notes"
                  value={formGeneralNotes}
                  onChange={(e) => setFormGeneralNotes(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  placeholder="Any general notes for the entire prescription..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#22262B] font-semibold py-2.5 px-5 rounded-md border border-[#E4E1D8] transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200"
                >
                  {currentPrescription ? 'Save Changes' : 'Create Prescription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionManagement;
